import {useCallback, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, TextField, Typography} from '@mui/material';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {
  SeniorityBand,
  SeniorityExpectations,
} from '../../types/DepartmentTargetProfileTypes';

const SENIORITY_BANDS: SeniorityBand[] = ['junior', 'mid_level', 'senior', 'lead', 'manager'];

export interface Props {
  initialExpectations: SeniorityExpectations;
  isSaving: boolean;
  onSave: (expectations: SeniorityExpectations) => void;
  hideTitle?: boolean;
}

const normalizeForSave = (draft: Record<SeniorityBand, string>): SeniorityExpectations => {
  const next: SeniorityExpectations = {};
  SENIORITY_BANDS.forEach((band) => {
    const text = draft[band]?.trim();
    if (text) {
      next[band] = text;
    }
  });
  return next;
};

export const SeniorityExpectationsEditor = ({
  initialExpectations,
  isSaving,
  onSave,
  hideTitle = false,
}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'companySettings.departments.modal.targetProfiles.seniorityExpectations',
  });
  const {t: tSeniority} = useTranslation('translation', {keyPrefix: 'shared.seniorityLevels'});

  const [draft, setDraft] = useState<Record<SeniorityBand, string>>(() => ({
    junior: initialExpectations.junior ?? '',
    mid_level: initialExpectations.mid_level ?? '',
    senior: initialExpectations.senior ?? '',
    lead: initialExpectations.lead ?? '',
    manager: initialExpectations.manager ?? '',
  }));

  useEffect(() => {
    setDraft({
      junior: initialExpectations.junior ?? '',
      mid_level: initialExpectations.mid_level ?? '',
      senior: initialExpectations.senior ?? '',
      lead: initialExpectations.lead ?? '',
      manager: initialExpectations.manager ?? '',
    });
  }, [initialExpectations]);

  const handleChange = useCallback((band: SeniorityBand, value: string) => {
    setDraft((current) => ({...current, [band]: value}));
  }, []);

  const handleSave = useCallback(() => {
    onSave(normalizeForSave(draft));
  }, [draft, onSave]);

  const hasChanges = useMemo(() => {
    const normalized = normalizeForSave(draft);
    return JSON.stringify(normalized) !== JSON.stringify(initialExpectations ?? {});
  }, [draft, initialExpectations]);

  return (
    <Box sx={{mb: hideTitle ? 0 : 1, mt: hideTitle ? 0 : 2}}>
      {!hideTitle && (
        <Typography variant='subtitle2' sx={{fontWeight: 700, color: greenColors[900], mb: 0.5}}>
          {t('title')}
        </Typography>
      )}
      {!hideTitle && (
        <Typography variant='body2' color='text.secondary' sx={{mb: 1.5}}>
          {t('helper')}
        </Typography>
      )}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
        {SENIORITY_BANDS.map((band) => (
          <TextField
            key={band}
            label={tSeniority(band, {defaultValue: band})}
            value={draft[band]}
            onChange={(event) => handleChange(band, event.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{maxLength: 2000}}
          />
        ))}
      </Box>
      <Box sx={{mt: 1.5, display: 'flex', justifyContent: 'flex-end'}}>
        <EmpionButton variant='primary' onClick={handleSave} disabled={!hasChanges || isSaving}>
          {t('save')}
        </EmpionButton>
      </Box>
    </Box>
  );
};
